import { ICellRendererParams } from 'ag-grid-community';
import { ComponentSelectorResult } from 'ag-grid-community/dist/lib/components/framework/userComponentFactory';
import { isEmpty, isNil, set, isFunction, get, trim } from 'lodash';

import {
  CELL_RENDERER,
  PredicateFn,
  ToolbarButtonDefinition,
} from '@shared/types';
import { XuxGridComponent } from '@geniuspos/ui-shared/modules/xdm-shared-components/components';
import { XuxEditGridComponent } from '@app/components/xux/xux-edit-grid/xux-edit-grid-widget.component';
import { XUXListContainerComponent } from '@app/containers/base/list/xux-list.container.component';

/**
 * get cell empty renderer
 *
 * @param targetDataPath target value path
 * @param valueRenderer renderer when target value is not empty
 * @param isTreeView is TreeView
 * @returns cellRenderer
 */
const getEmptyHiddenCellSelector = (
  targetDataPath: string,
  valueRenderer?: CELL_RENDERER,
  isTreeView: boolean = false,
): ((params: ICellRendererParams) => ComponentSelectorResult) => {
  return (params: ICellRendererParams): ComponentSelectorResult => {
    if (isTreeView && !params.data.isLeaf) {
      return { component: CELL_RENDERER.empty_renderer };
    }

    if (!isEmpty(params.data?.[targetDataPath])) {
      return { component: valueRenderer };
    }

    return { component: CELL_RENDERER.empty_renderer };
  };
};

/**
 * use this function to update grid arrow keyboard support
 *
 * @param grid target grid(xux grid)
 * @param isEnabled is enable keyboard support or not
 */
const setGridKeyboardSupport = (
  grid: XuxGridComponent,
  isEnabled: boolean,
): void => {
  if (isNil(grid)) {
    return;
  }

  const optionValue = !isEnabled;

  set(grid.gridOptions, 'suppressCellSelection', optionValue);
  set(grid.gridOptions, 'suppressKeyboardEvent', (): boolean => optionValue);
};

const getCombinedToolbarButtonHidden = (
  originHidden: ToolbarButtonDefinition['hidden'],
  additionHidden: ToolbarButtonDefinition['hidden'],
): PredicateFn<boolean> => {
  return (value, action): boolean => {
    const isHidden = (hidden: ToolbarButtonDefinition['hidden']): boolean =>
      isFunction(hidden) ? hidden(value, action) : hidden;

    return isHidden(originHidden) || isHidden(additionHidden);
  };
};

const getGridFilterStr = (
  xuxGrid: XuxEditGridComponent | XUXListContainerComponent,
): string =>
  trim(get(xuxGrid, 'xuxGridComponent.actionPanel.quickFilterControl.value'));

export {
  getEmptyHiddenCellSelector,
  setGridKeyboardSupport,
  getCombinedToolbarButtonHidden,
  getGridFilterStr,
};
